import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { activityKeys } from '@/features/activities';
import { dashboardQueryKeys } from '../api/dashboard.api';
import { MDDashboard } from '../components/MDDashboard';
import { PartnerDashboard } from '../components/PartnerDashboard';
import { AccountantDashboard } from '../components/AccountantDashboard';
import { LawyerDashboard } from '../components/LawyerDashboard';
import { YearlyPlanModal } from '../components/YearlyPlanModal';
import { useYearlyPlan } from '../hooks/useYearlyPlan';
import type { StatisticPeriod } from '../api/dashboard.api';

type DashboardRole = 'md' | 'partner' | 'accountant' | 'lawyer';

function PeriodSelector({ value, onChange }: { value: StatisticPeriod; onChange: (p: StatisticPeriod) => void }) {
  const periods: StatisticPeriod[] = ['YTD', 'MTD', 'QTD', 'HY'];
  return (
    <div className="flex w-fit items-center border border-slate-200 rounded-md shadow-xs overflow-hidden">
      {periods.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={cn(
            'px-4 py-1.5 border-r border-slate-200 last:border-r-0 text-[0.8125rem] font-medium transition-colors cursor-pointer',
            value === p
              ? 'bg-[#E6F4FF] text-[#0069D1] font-semibold'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          )}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation('common');
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const queryClient = useQueryClient();
  // Spins the refresh icon while ANY dashboard query is in flight.
  const dashboardFetching = useIsFetching({ queryKey: dashboardQueryKeys.all });

  const handleRefresh = () => {
    // Refetch every mounted dashboard query (all roles share the 'dashboard'
    // key root) + the running-timer state the lawyer tables read. No reload.
    void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.all });
    void queryClient.invalidateQueries({ queryKey: activityKeys.active() });
  };

  const canMD         = hasAccess('md-statistic', 'get');
  const canPartner    = hasAccess('partner-statistic', 'get');
  const canAccountant = hasAccess('accountant-statistic', 'get');
  const canLawyer     = hasAccess('lawyer-statistic', 'get');
  const canManageYearlyPlan = hasAccess('yearly-plan', 'create');

  const defaultRole: DashboardRole =
    canMD ? 'md' : canPartner ? 'partner' : canAccountant ? 'accountant' : 'lawyer';

  const [activeRole, setActiveRole] = useState<DashboardRole>(defaultRole);
  const [period, setPeriod] = useState<StatisticPeriod>('YTD');
  const [yearlyPlanOpen, setYearlyPlanOpen] = useState(false);

  // Surface the current target amount on the MD "Yearly Plan" button.
  const showYearlyPlanBtn = canManageYearlyPlan && activeRole === 'md';
  const { data: yearlyPlan } = useYearlyPlan(showYearlyPlanBtn);
  const yearlyPlanLabel =
    yearlyPlan?.amount != null
      ? `${t('dashboard.yearlyPlan')} ($${yearlyPlan.amount.toLocaleString()})`
      : t('dashboard.yearlyPlan');

  if (!canMD && !canPartner && !canAccountant && !canLawyer) {
    return <Navigate to="/app/forbidden" replace />;
  }

  const tabs: { key: DashboardRole; label: string }[] = [
    ...(canMD         ? [{ key: 'md'         as DashboardRole, label: t('dashboard.roles.md') }]         : []),
    ...(canPartner    ? [{ key: 'partner'    as DashboardRole, label: t('dashboard.roles.partner') }]    : []),
    ...(canAccountant ? [{ key: 'accountant' as DashboardRole, label: t('dashboard.roles.accountant') }] : []),
    ...(canLawyer     ? [{ key: 'lawyer'     as DashboardRole, label: t('dashboard.roles.lawyer') }]     : []),
  ];

  const roleTitles: Record<DashboardRole, string> = {
    md:         t('dashboard.mdTitle'),
    partner:    t('dashboard.partnerTitle'),
    accountant: t('dashboard.accountantTitle'),
    lawyer:     t('dashboard.lawyerTitle'),
  };

  const renderContent = () => {
    switch (activeRole) {
      case 'partner':    return <PartnerDashboard />;
      case 'accountant': return <AccountantDashboard period={period} />;
      case 'lawyer':     return <LawyerDashboard />;
      default:           return <MDDashboard period={period} />;
    }
  };

  return (
    // pb-24 keeps the last dashboard block off the bottom edge of the screen
    <div className="min-h-full bg-[#F5F6F7] pb-24">
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">

        {tabs.length > 1 ? (
          <div className="flex gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveRole(tab.key)}
                className={cn(
                  'px-4 py-1.5 border text-[0.8125rem] font-medium rounded-md transition-all cursor-pointer',
                  activeRole === tab.key
                    ? 'bg-[#0069D1] text-white border-[#0069D1]'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-[#0069D1] hover:text-[#0069D1]'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : (
          <h1 className="text-xl font-semibold text-slate-900">
            {roleTitles[activeRole]}
          </h1>
        )}

        <div className="flex items-center gap-3">
          {showYearlyPlanBtn && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-[#0069D1] border-[#0069D1] hover:bg-[#E6F4FF]"
              onClick={() => setYearlyPlanOpen(true)}
            >
              <Target className="h-4 w-4" />
              {yearlyPlanLabel}
            </Button>
          )}
          {activeRole === 'md' && <PeriodSelector value={period} onChange={setPeriod} />}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleRefresh}
            disabled={dashboardFetching > 0}
            title={t('actions.refresh')}
          >
            <RefreshCw className={cn('h-4 w-4', dashboardFetching > 0 && 'animate-spin')} />
            {t('actions.refresh')}
          </Button>
        </div>
      </div>

      {/* Case 360° (Stage 12) cards live inside each role dashboard's
          DraggableBlocksContainer so they reorder like every other block. */}
      {renderContent()}

      {canManageYearlyPlan && (
        <YearlyPlanModal open={yearlyPlanOpen} onOpenChange={setYearlyPlanOpen} />
      )}
    </div>
  );
}
